/* eslint-disable max-lines */
import {
  BarsOutlined,
  DashboardOutlined,
  LogoutOutlined,
  SettingOutlined,
  UnorderedListOutlined,
  VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import { RefineThemedLayoutSiderProps, ThemedTitle, useThemedLayoutContext } from '@refinedev/antd';
import {
  CanAccess,
  type TreeMenuItem,
  useIsExistAuthentication,
  useLink,
  useLogout,
  useMenu,
  useTranslate,
  useWarnAboutChange,
} from '@refinedev/core';
import { useCurrentUser } from '@hooks/use-current-user';
import { Button, ConfigProvider, Drawer, Grid, Layout, Menu, theme } from 'antd';
import React, { CSSProperties, useContext } from 'react';

const drawerButtonStyles: CSSProperties = {
  borderStartStartRadius: 0,
  borderEndStartRadius: 0,
  position: 'fixed',
  top: 56,
  zIndex: 999,
};

const DefaultTitle = () => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <img alt="App Logo" height={29} src="/images/headerLogo.png" width={182} />
  </div>
);

export const ThemedSiderV2: React.FC<RefineThemedLayoutSiderProps> = ({
  Title: TitleFromProps,
  render,
  meta,
  fixed,
  activeItemDisabled = false,
}) => {
  const { token } = theme.useToken();
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } =
    useThemedLayoutContext();

  const isExistAuthentication = useIsExistAuthentication();
  const direction = useContext(ConfigProvider.ConfigContext)?.direction;
  const Link = useLink();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const t = useTranslate();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu({ meta });
  const currentUser = useCurrentUser();
  const breakpoint = Grid.useBreakpoint();
  const { mutate: mutateLogout } = useLogout();

  const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;

  const RenderToTitle = TitleFromProps ?? DefaultTitle ?? ThemedTitle;

  const menuItemStyles = {
    border: '1px solid',
    borderColor: 'transparent',
    height: '38px',
  };

  const renderTreeView = (tree: TreeMenuItem[], selectedKey?: string) => {
    return tree.map((item: TreeMenuItem) => {
      const { icon, label, route, key, name, children, meta } = item;

      if (children.length > 0) {
        return (
          <CanAccess
            key={item.key}
            action="list"
            params={{
              resource: item,
            }}
            resource={name}
          >
            <Menu.SubMenu key={item.key} icon={icon ?? <UnorderedListOutlined />} title={label}>
              {renderTreeView(children, selectedKey)}
            </Menu.SubMenu>
          </CanAccess>
        );
      }
      const isSelected = key === selectedKey;
      const isRoute = !(meta?.parent !== undefined && children.length === 0);

      const linkStyle: React.CSSProperties = {
        ...(activeItemDisabled && isSelected ? { pointerEvents: 'none' } : {}),
      };

      return (
        <CanAccess
          key={item.key}
          action="list"
          params={{
            resource: item,
          }}
          resource={name}
        >
          <Menu.Item
            key={item.key}
            icon={icon ?? (isRoute && <UnorderedListOutlined />)}
            style={{
              ...menuItemStyles,
              ...linkStyle,
              ...(isSelected && { borderColor: token.colorPrimaryBorder }),
            }}
          >
            <Link style={linkStyle} to={route ?? ''}>
              {label}
            </Link>
            {!siderCollapsed && isSelected && <div className="ant-menu-tree-arrow" />}
          </Menu.Item>
        </CanAccess>
      );
    });
  };

  const handleLogout = () => {
    if (warnWhen) {
      const confirm = window.confirm(
        t('warnWhenUnsavedChanges', 'Are you sure you want to leave? You have unsaved changes.'),
      );

      if (confirm) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  const logout = isExistAuthentication && (
    <Menu.Item
      key="logout"
      icon={<LogoutOutlined />}
      style={menuItemStyles}
      onClick={() => handleLogout()}
    >
      {t('buttons.logout', 'Logout')}
    </Menu.Item>
  );

  const dashboard = (
    <Menu.Item key="dashboard" icon={<DashboardOutlined />} style={menuItemStyles}>
      <Link to="/">{t('dashboard.title', 'Dashboard')}</Link>
      {!siderCollapsed && selectedKey === '/' && <div className="ant-menu-tree-arrow" />}
    </Menu.Item>
  );

  const items = renderTreeView(
    //settings should be rendered in a separate shared section
    menuItems.filter(item => item.key !== '/settings' && item.key !== '/dashboard'),
    selectedKey,
  );

  const settings = (
    <Menu.Item key="settings" icon={<SettingOutlined />}>
      <Link to="/settings">{t('settings.title', 'Settings')}</Link>
    </Menu.Item>
  );

  const sharedBottomSection = (
    <>
      {settings}
      {logout}
    </>
  );

  const renderSider = () => {
    if (render) {
      return render({
        items,
        logout,
        collapsed: siderCollapsed,
      });
    }

    return (
      <>
        {dashboard}
        {items}
      </>
    );
  };

  const renderMenu = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'space-between',
          paddingBottom: '50px',
        }}
      >
        <Menu
          key={currentUser?.sub}
          defaultOpenKeys={defaultOpenKeys}
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          style={{
            flex: '1',
            border: 'none',
            overflow: 'auto',
            backgroundColor: token.colorBgBase,
            paddingTop: '32px',
          }}
          onClick={() => {
            setMobileSiderOpen(false);
          }}
        >
          {renderSider()}
        </Menu>
        <Menu
          mode="inline"
          style={{
            border: 'none',
            backgroundColor: token.colorBgBase,
          }}
        >
          {sharedBottomSection}
        </Menu>
      </div>
    );
  };

  const renderDrawerSider = () => {
    return (
      <>
        <Drawer
          closable={false}
          maskClosable={true}
          open={mobileSiderOpen}
          placement={direction === 'rtl' ? 'right' : 'left'}
          styles={{
            body: { padding: 0 },
          }}
          width={236}
          onClose={() => setMobileSiderOpen(false)}
        >
          <Layout>
            <Layout.Sider
              style={{
                height: '100vh',
                backgroundColor: token.colorBgBase,
              }}
            >
              <div
                style={{
                  width: '236px',
                  padding: '0 16px',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  height: '56px',
                  backgroundColor: token.colorBgElevated,
                }}
              >
                <RenderToTitle collapsed={false} />
              </div>
              {renderMenu()}
            </Layout.Sider>
          </Layout>
        </Drawer>
        <Button
          icon={<BarsOutlined />}
          size="large"
          style={drawerButtonStyles}
          onClick={() => setMobileSiderOpen(true)}
        />
      </>
    );
  };

  if (isMobile) {
    return renderDrawerSider();
  }

  const siderStyles: React.CSSProperties = {
    backgroundColor: token.colorBgBase,
    width: 236,
  };

  if (fixed) {
    siderStyles.position = 'fixed';
    siderStyles.top = 0;
    siderStyles.height = '100vh';
    siderStyles.zIndex = 999;
  }
  const renderClosingIcons = () => {
    const OpenIcon =
      direction === 'rtl' ? VerticalAlignBottomOutlined : VerticalAlignBottomOutlined;
    const CollapsedIcon =
      direction === 'rtl' ? VerticalAlignBottomOutlined : VerticalAlignBottomOutlined;
    const IconComponent = siderCollapsed ? CollapsedIcon : OpenIcon;

    return (
      <IconComponent style={{ transform: siderCollapsed ? 'rotate(-90deg)' : 'rotate(90deg)' }} />
    );
  };

  return (
    <>
      {fixed && (
        <div
          style={{
            width: siderCollapsed ? '80px' : '236px',
            transition: 'all 0.2s',
          }}
        />
      )}
      <Layout.Sider
        breakpoint="lg"
        collapsed={siderCollapsed}
        collapsedWidth={80}
        collapsible
        style={siderStyles}
        trigger={
          <Button
            style={{
              borderRadius: 0,
              height: '100%',
              width: '100%',
              backgroundColor: token.colorBgBase,
            }}
            type="text"
          >
            {renderClosingIcons()}
          </Button>
        }
        width={236}
        onCollapse={(collapsed, type) => {
          if (type === 'clickTrigger') {
            setSiderCollapsed(collapsed);
          }
        }}
      >
        <div
          style={{
            width: siderCollapsed ? '80px' : '236px',
            padding: siderCollapsed ? '0' : '0 16px',
            objectFit: 'none',
            objectPosition: 'left',
            display: 'flex',
            justifyContent: siderCollapsed ? 'center' : 'flex-start',
            alignItems: 'center',
            height: '56px',
            backgroundColor: token.colorBgElevated,
            fontSize: '14px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <div
            style={{
              width: siderCollapsed ? '50px' : 'auto',
              overflow: siderCollapsed ? 'hidden' : 'initial',
            }}
          >
            <RenderToTitle collapsed={siderCollapsed} />
          </div>
        </div>
        {renderMenu()}
      </Layout.Sider>
    </>
  );
};
